import React from 'react';
import { Info, Check, X } from 'lucide-react';

/**
 * Guest selection block for ParticipantBookingPanel — shared verbatim by
 * both create and edit mode. The participant row's checkbox is the sole
 * control for guest-only status: clicking it directly toggles isGuestOnly
 * (checked = participant is staying in the room; unchecked = guest-only).
 */
const GuestPicker = ({
  guests,
  selectedGuestIds,
  isGuestOnly,
  participantName,
  onToggleGuest,
  onToggleGuestOnly,
}) => (
  <div className="pbt-guest-block">
    {participantName && (
      <div className="pbt-guest-row pbt-guest-row--participant">
        <button
          type="button"
          className={`pbt-guest-row__toggle${!isGuestOnly ? ' pbt-guest-row__toggle--on' : ''}`}
          onClick={() => onToggleGuestOnly(!isGuestOnly)}
          aria-pressed={!isGuestOnly}
          aria-label={`${isGuestOnly ? 'Include' : 'Remove'} ${participantName} from this room`}
        >
          {!isGuestOnly ? <Check size={12} strokeWidth={3} /> : <X size={12} strokeWidth={3} />}
        </button>
        <span>{participantName} (participant)</span>
      </div>
    )}

    <div className="pbt-guest-block__heading">
      <span>Add Guests</span>
      <span className="pbt-guest-block__heading-optional">(optional)</span>
      <span
        className="pbt-guest-info-icon"
        data-tooltip="A guest usually stays with the participant. Uncheck the participant row above if the participant is not staying in this room."
        tabIndex={0}
        role="img"
        aria-label="A guest usually stays with the participant. Uncheck the participant row above if the participant is not staying in this room."
      >
        <Info size={13} />
      </span>
    </div>
    {selectedGuestIds.length > 0 && (
      <div className="pbt-guest-pills">
        {selectedGuestIds.map((id) => {
          const g = guests.find((x) => x._id === id);
          if (!g) return null;
          return (
            <span key={id} className="pbt-guest-pill">
              {`${g.firstName || ''} ${g.lastName || ''}`.trim()}
              <button type="button" className="pbt-guest-pill__remove" onClick={() => onToggleGuest(id)}>×</button>
            </span>
          );
        })}
      </div>
    )}
    {guests.length > 0 ? (
      <ul className="pbt-guest-block__list">
        {guests.map((g) => {
          const selected = selectedGuestIds.includes(g._id);
          return (
            <li key={g._id} className="pbt-guest-row">
              <button
                type="button"
                className={`pbt-guest-row__toggle${selected ? ' pbt-guest-row__toggle--on' : ''}`}
                onClick={() => onToggleGuest(g._id)}
                aria-pressed={selected}
                aria-label={`${selected ? 'Remove' : 'Add'} ${`${g.firstName || ''} ${g.lastName || ''}`.trim() || 'guest'}`}
              >
                {selected ? <Check size={12} strokeWidth={3} /> : <X size={12} strokeWidth={3} />}
              </button>
              <span>{`${g.firstName || ''} ${g.lastName || ''}`.trim() || '(No name)'}</span>
            </li>
          );
        })}
      </ul>
    ) : (
      <div className="pbt-guest-block__empty">No guests registered.</div>
    )}
    {isGuestOnly && (
      <div className="pbt-guest-only-notice">
        <span className="pbt-guest-only-notice__icon"><Info size={13} /></span>
        <span>This will be booked as a guest-only room</span>
      </div>
    )}
  </div>
);

export default GuestPicker;
